import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const PasswordlessLogin = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [redirectTo, setRedirectTo] = useState('');
  const [oneTimeCode, setOneTimeCode] = useState("");
  const [isCodeLoading, setIsCodeLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // Get redirectTo from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get('redirectTo');
    if (redirect) {
      setRedirectTo(decodeURIComponent(redirect));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setIsLoading(true);
    setIsSuccess(false);

    if (!email) {
      setMessage('Email is required');
      setIsLoading(false);
      return;
    }

    try {
      // Include redirectTo in the magic link request
      const response = await axios.post('/api/auth/request-magic-link', {
        email,
        redirectTo: redirectTo || '/browse-candidates'
      });
      
      setMessage(response.data.message);
      setIsSuccess(true);
    } catch (err) {
      if (err.response && err.response.data) {
        setMessage(err.response.data.message);
        // Don't set as success for error cases
        setIsSuccess(false);
      } else {
        setMessage('Failed to send magic link');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setIsCodeLoading(true);

    if (!oneTimeCode) {
      setMessage('One-time code is required');
      setIsCodeLoading(false);
      return;
    }

    try {
      const response = await axios.post('/api/auth/verify-one-time-code', {
        email,
        code: oneTimeCode,
        remember: false
      });

      // Store the token and redirect
      localStorage.setItem('token', response.data.token);
      
      // Check onboarding status and redirect accordingly
      let destination = redirectTo || '/browse-candidates';
      if (response.data.onboarding && !response.data.onboarding.isComplete) {
        // If onboarding is incomplete, redirect to onboarding form
        const companyId = response.data.user.companyId;
        destination = companyId ? `/new-onboarding?companyId=${companyId}` : '/new-onboarding';
        console.log(`Onboarding incomplete for company ${response.data.onboarding.companyName}. Redirecting to onboarding.`);
      }
      
      navigate(destination);
    } catch (err) {
      if (err.response && err.response.data) {
        setMessage(err.response.data.message);
      } else {
        setMessage('Failed to verify code');
      }
    } finally {
      setIsCodeLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Jumpstart logo at top left */}
      <div className="absolute top-6 left-6">
        <img
          src="https://www.jumpstart-uk.com/wp-content/uploads/2022/05/jumpstart-logo-black.svg"
          className="h-8"
          alt="Jumpstart Logo"
        />
      </div>

      {/* Main content centered */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-md">
          {!isSuccess ? (
            // Initial magic link request form
            <>
              {/* Clean Header */}
              <div className="text-center mb-10">
                <h1 className="text-4xl font-normal text-gray-900 mb-4">
                  Welcome to Jumpstart
                </h1>
                <p className="text-lg text-gray-500 leading-relaxed">
                  Sign in with just your email 
                </p>
              </div>

              {/* Clean Form */}
              <div className="space-y-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Clean Email field */}
                  <div>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full px-4 py-4 text-base border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0fb592] focus:border-transparent placeholder-gray-400 bg-white transition-all duration-200"
                      required
                      disabled={isLoading}
                    />
                  </div>



                  {/* Clean send button */}
                  <button
                    type="submit"
                    disabled={isLoading || !email.trim()}
                    className="w-full bg-[#0fb592] text-white py-4 px-4 text-base font-medium rounded-xl hover:bg-[#0da578] focus:outline-none focus:ring-2 focus:ring-[#0fb592] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      "Continue"
                    )}
                  </button>
                </form>

                {/* Clean Error message display */}
                {message && !isSuccess && (
                  <div className="p-4 rounded-lg bg-red-50 text-red-700">
                    <div className="text-sm">
                      {message}
                    </div>
                  </div>
                )}
              </div>


            </>
          ) : (
            // Success state - Clean minimal design
            <>
              {/* Clean Header */}
              <div className="text-center mb-10">
                <h1 className="text-4xl font-normal text-gray-900 mb-4">
                  Check your inbox!
                </h1>
                <p className="text-lg text-gray-500 leading-relaxed">
                  We've just emailed you a temporary password.<br />
                  Please enter it below.
                </p>
              </div>

              {/* Clean Email display */}
              <div className="mb-8">
                <div className="flex items-center justify-center space-x-3 text-gray-700">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-base">{email}</span>
                </div>
              </div>

              {/* Clean Code input form */}
              <div className="mb-8">
                <form onSubmit={handleCodeSubmit} className="space-y-6">
                  <div>
                    <input
                      type="text"
                      id="oneTimeCode"
                      value={oneTimeCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                        setOneTimeCode(value);
                      }}
                      placeholder="Enter temporary password..."
                      maxLength={6}
                      className="w-full px-4 py-4 text-base border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0fb592] focus:border-transparent placeholder-gray-400 bg-white transition-all duration-200"
                      disabled={isCodeLoading}
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isCodeLoading || oneTimeCode.length !== 6}
                    className="w-full bg-[#0fb592] text-white py-4 px-4 text-base font-medium rounded-xl hover:bg-[#0da578] focus:outline-none focus:ring-2 focus:ring-[#0fb592] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isCodeLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verifying...
                      </span>
                    ) : (
                      "Continue"
                    )}
                  </button>
                </form>

                {/* Clean Error message display */}
                {message && !message.includes('sent') && (
                  <div className="mt-4 p-4 rounded-lg bg-red-50 text-red-700">
                    <div className="text-sm">
                      {message}
                    </div>
                  </div>
                )}
              </div>

              {/* Clean Try again link */}
              <div className="text-center">
                <button
                  onClick={() => {
                    setIsSuccess(false);
                    setMessage("");
                    setEmail("");
                    setOneTimeCode("");
                  }}
                  className="text-gray-500 hover:text-gray-700 text-sm underline transition-colors duration-200"
                >
                  Try another email
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-6">
        <p className="text-gray-500 text-xs">
          By continuing, you agree to our{" "}
          <a href="https://jumpstart-alumni.notion.site/Terms-for-Startup-Partners-127b8f57dd84802ca447cfd81a65ff90" target="_blank" rel="noopener noreferrer" className="underline">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="https://jumpstart-alumni.notion.site/Terms-for-Startup-Partners-127b8f57dd84802ca447cfd81a65ff90" target="_blank" rel="noopener noreferrer" className="underline">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default PasswordlessLogin; 